import * as R from 'ramda'
import * as M from '../Map'
import type {NEList} from '../NEList'
import type {SetCreationParameter} from '../Set'
import * as S from '../Set'

/**
 * The shape produced by {@link groupByDiscriminant}: an object keyed by every possible value of the
 * discriminant `D`, where each key's value is a non-empty list of *only* the union members that
 * carry that tag. Keys are optional because a given tag may be absent from the input list.
 */
export type TGroupedByDiscriminant<
  V extends Record<D, PropertyKey>,
  D extends keyof V,
> = {
  [K in V[D]]?: NEList<Extract<V, Record<D, K>>>
}

/**
 * Like {@link M.groupBy}, but groups a discriminated-union array by one of its discriminant
 * properties and *retains* per-key type information. A `Map<K, V[]>` can't express this (a Map has a
 * single value type), so the result is a Record, letting each key carry its own narrowed value type.
 *
 * For a `AnyMajorAction[]` grouped by `'type'`:
 *
 *     const grouped = groupByDiscriminant('type', availableMoves)
 *     grouped.slyDeal // MajorActionSlyDeal[] | undefined  (not AnyMajorAction[])
 *
 * NOTE: this only narrows if the union `V` is genuinely discriminated by `D` at the *type* level —
 * i.e. each member has a distinct literal `D`. If every member widens `D` to the full union, every
 * group narrows to `never[]` (see the `TWidened` case in index.test-d.ts).
 */
export const groupByDiscriminant = <
  V extends Record<D, PropertyKey>,
  D extends keyof V,
>(
  discriminant: D,
  list: ReadonlyArray<V>
): TGroupedByDiscriminant<V, D> => {
  const grouped: Partial<Record<PropertyKey, V[]>> = {}
  list.forEach(item => {
    const key = item[discriminant]
    const existing = grouped[key]
    if (existing) {
      existing.push(item)
    } else {
      grouped[key] = [item]
    }
  })
  // The cast is validated by index.test-d.ts. TS can't prove that bucketing by `item[discriminant]`
  // lands each member under its own tag, but the runtime guarantees it.
  return grouped as TGroupedByDiscriminant<V, D>
}

/** Get a Set of the keys of a given Record object */
export const recordKeySet = <K extends string | number | symbol>(
  obj: Record<K, any>
): Set<K> => {
  return M.keySet(M.fromRecord(obj))
}

/** Get an Array of the keys of a given Record object */
export const recordKeys = <K extends string | number | symbol>(
  obj: Record<K, any>
): K[] => {
  return M.keys(M.fromRecord(obj))
}

/** Like {@link recordKeys}, but for partial records. */
export const partialRecordKeys = <K extends string | number | symbol>(
  obj: Partial<Record<K, unknown>>
): K[] => {
  // safe: `fromRecord` only reads own-enumerable keys, which a partial record has at runtime
  return M.keys(M.fromRecord(obj as Record<K, unknown>))
}

/** Get an Array of the key/value pairs of a given Record object */
export const recordEntries = <K extends string | number | symbol, V>(
  obj: Record<K, V>
): [K, V][] => {
  return M.entries(M.fromRecord(obj))
}

/**
 * Takes a Record object and switches the keys and values. To account for duplicate values in the original Record,
 *   the values in the new Record will be an array.
 * (Same as R.invert, with better typing for Record objects.)
 */
export const invertRecord = <
  T extends string | number | symbol,
  U extends string | number | symbol,
>(
  obj: Record<T, U>
): {[key in U]: T[]} => {
  return R.invert(obj) as {[key in U]: T[]}
}

export const invertRecordFlat = <
  T extends string | number | symbol,
  U extends string | number | symbol,
>(
  obj: Record<T, U>
): {[key in U]: T} => {
  return R.invertObj(obj as any) as {[key in U]: T}
}

/** Return a partial copy of the given record only including the specified keys. */
export const filterRecord = <K extends string, V extends any>(
  keys: SetCreationParameter<K>, // keys to keep
  record: Record<K, V>
): Partial<Record<K, V>> => {
  // gave up on the types here, but the test validates the cast
  return R.pick(S.values(S.create(keys)), record) as any
}

/** Return a partial copy of the given record after removing the specified keys. */
export const rejectRecord = <K extends string, V extends any>(
  keys: SetCreationParameter<K>, // keys to remove
  record: Record<K, V>
): Partial<Record<K, V>> => {
  // gave up on the types here, but the test validates the cast
  return R.omit(S.values(S.create(keys)), record) as any
}

/** Change the values at each key of a record with the given function. */
export const mapRecord = <K extends string, V extends any, U extends any>(
  mutate: (v: V, k: K, index: number) => U,
  record: Record<K, V>
): Record<K, U> => {
  const retVal = {} as Record<K, U>
  recordEntries(record).forEach(
    (pair, i) => (retVal[pair[0]] = mutate(pair[1], pair[0], i))
  )
  return retVal
}
